import React, { useCallback, useEffect, useRef, useState } from 'react';
import MfaEnrol from './components/MfaEnrol';
import { INPUT, PRIMARY, SECONDARY, ALERT } from './auth/mfaStyles';

export const MFA_OFF_NOTICE = 'Two-factor is off. Sign in again.';
const ROLE_REQUIRED = 'Your role requires two-factor sign-in';

// The user's own two-factor state. Sign-out after turn-off is the caller's job (every session is revoked).
export default function SecuritySettings({ token, apiUrl = '', onSignedOut, onLogoutAll }) {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const codeInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/auth/mfa/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setStatus(await res.json());
      setLoadError('');
    } catch {
      setLoadError('Could not load your security settings. Reload to try again.');
    }
  }, [apiUrl, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (disabling) codeInput.current?.focus(); }, [disabling, error]);

  const turnOff = async (e) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError('');
    const res = await fetch(`${apiUrl}/api/auth/mfa/disable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ code: code.trim() }),
    }).catch(() => null);
    if (res && (res.status === 204 || res.ok)) { onSignedOut(MFA_OFF_NOTICE); return; }
    const detail = res ? (await res.json().catch(() => ({}))).detail : '';
    setBusy(false);
    setCode('');
    setError(res?.status === 403 ? ROLE_REQUIRED : (typeof detail === 'string' && detail) || 'Could not turn off two-factor.');
  };

  const enrolled = () => { setEnrolling(false); load(); };
  const low = status?.enabled && status.recovery_codes_remaining <= 2;

  return (
    <div className="max-w-xl mx-auto px-4 py-8 space-y-6" data-testid="security-settings">
      <h1 className="text-2xl font-bold text-gray-900">Security</h1>
      {loadError && <div role="alert" className={ALERT}>{loadError}</div>}
      {status && (
        <section className="bg-white rounded-2xl shadow-sm p-6 space-y-4" aria-labelledby="security-mfa-heading">
          <h2 id="security-mfa-heading" className="text-lg font-semibold text-gray-900">Two-factor sign-in</h2>
          <p className="text-sm" data-testid="security-mfa-status">Status: <strong>{status.enabled ? 'On' : 'Off'}</strong></p>
          {status.enabled && (
            <p className="text-sm text-gray-700" data-testid="security-recovery-remaining">
              Recovery codes remaining: {status.recovery_codes_remaining}
            </p>
          )}
          {low && (
            <p className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg" data-testid="security-recovery-warning">
              Only {status.recovery_codes_remaining} recovery codes left. Keep them somewhere safe.
            </p>
          )}
          {status.required_for_you && <p className="text-sm text-gray-700" data-testid="security-required-note">{ROLE_REQUIRED}.</p>}
          {error && <div role="alert" className={ALERT} data-testid="security-error">{error}</div>}
          {!status.enabled && !enrolling && (
            <button type="button" className={PRIMARY} data-testid="security-enable-btn" onClick={() => setEnrolling(true)}>Turn on two-factor</button>
          )}
          {!status.enabled && enrolling && (
            <MfaEnrol authHeader={token} apiUrl={apiUrl} onDone={enrolled} onCancel={() => setEnrolling(false)} />
          )}
          {status.enabled && !status.required_for_you && !disabling && (
            <button type="button" className={SECONDARY} data-testid="security-disable-btn" onClick={() => setDisabling(true)}>Turn off</button>
          )}
          {disabling && (
            <form onSubmit={turnOff} className="space-y-3" noValidate>
              <label htmlFor="security-disable-code" className="block text-sm font-medium text-gray-700">Current 6-digit code</label>
              <input id="security-disable-code" data-testid="security-disable-code" ref={codeInput} className={INPUT} type="text"
                inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value)} />
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={busy} className={SECONDARY} data-testid="security-disable-confirm">Turn off two-factor</button>
                <button type="button" className={SECONDARY} data-testid="security-disable-cancel"
                  onClick={() => { setDisabling(false); setError(''); setCode(''); }}>Cancel</button>
              </div>
            </form>
          )}
        </section>
      )}
      <section className="bg-white rounded-2xl shadow-sm p-6 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Sessions</h2>
        <p className="text-sm text-gray-600">Sign out everywhere, including this device.</p>
        <button type="button" className={SECONDARY} data-testid="security-logout-all-btn" onClick={onLogoutAll}>Log out of all devices</button>
      </section>
    </div>
  );
}
